import { type FC, useState } from "react";
import { Platform, Pressable, type ViewStyle } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../../theme";
import { Icon } from "../../Icon";
import type { PaginationNavDirection } from "../Pagination.types";
import { CHEVRON_SIZE, NAV_CONTROL_SIZE } from "../pagination-metrics";

export interface PaginationNavButtonProps {
  readonly direction: PaginationNavDirection;
  readonly disabled: boolean;
  readonly onPress: () => void;
  readonly accessibilityLabel: string;
}

const PaginationNavButton: FC<PaginationNavButtonProps> = ({ direction, disabled, onPress, accessibilityLabel }) => {
  const { theme } = useUnistyles();
  const [hovered, setHovered] = useState(false);

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      onHoverIn={() => {
        setHovered(true);
      }}
      onHoverOut={() => {
        setHovered(false);
      }}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled }}
      style={({ pressed }) => [
        styles.button,
        hovered && !disabled && styles.buttonHovered,
        pressed && !disabled && styles.buttonPressed,
        disabled && styles.buttonDisabled,
        Platform.OS === "web" && !disabled && styles.webInteractive,
      ]}
    >
      <Icon
        name={direction === "previous" ? "chevronLeft" : "chevronRight"}
        size={CHEVRON_SIZE}
        color={theme.colors.icon}
      />
    </Pressable>
  );
};

PaginationNavButton.displayName = "PaginationNavButton";

export { PaginationNavButton };

const styles = StyleSheet.create((theme) => ({
  button: {
    width: NAV_CONTROL_SIZE,
    height: NAV_CONTROL_SIZE,
    borderRadius: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    backgroundColor: theme.colors.surface2.background,
    alignItems: "center",
    justifyContent: "center",
  },
  buttonHovered: {
    backgroundColor: theme.colors.surface2.hover,
  },
  buttonPressed: {
    backgroundColor: theme.colors.surface2.selected,
  },
  // Kept in the row rather than hidden at the ends of the range: the chevrons
  // are the only controls whose position is stable, so removing them would
  // shift the page buttons under the cursor on the first and last page.
  buttonDisabled: {
    opacity: 0.35,
  },
  webInteractive: Object.assign({ cursor: "pointer" } satisfies ViewStyle, {
    transition: "background-color 0.15s ease-in-out",
  }),
}));
